/**
 * « IMPRIMER DES ÉTIQUETTES » depuis un bon ou une commande (maquette `peOn`).
 *
 * TOUT EST RÉEL : les modèles viennent du STUDIO d'étiquettes (`api.labels`), l'aperçu est rendu par
 * le MÊME `LabelPlate` que le studio, les valeurs sont RELUES du catalogue au moment d'imprimer
 * (`api.labelData` — entre le clic et l'impression un prix a pu changer), et l'impression part par
 * `buildPrintDocument` + `openPrintDocument` — le pipeline de l'écran Étiquettes, pas une copie.
 */
import { useEffect, useMemo, useState } from 'react';
import { Icon, Modal, showToast, Trans } from '@orgazone/ui-web';
import { api, type LabelSampleRow, type LabelTemplate } from '../api';
import { LabelPlate, labelValuesOf } from './etiquettes-render';
import { buildPrintDocument, countSheets, openPrintDocument, type PrintRow } from './etiquettes-print';
import { BarcodeWidthWarning, neutralPlateValues, UsedOnFirstSheetField } from './etiquettes-tunnel';
import { type LigneAEtiqueter } from './receptions-shared';
import { key, t, type Translatable } from '../i18n';

type SourceDeQuantite = 'done' | 'ctl' | 'wait';

const SOURCES_QTE: { k: SourceDeQuantite; l: Translatable }[] = [
  { k: 'done', l: key('Pièces déjà entrées en stock') },
  { k: 'ctl', l: key('Contrôlées, pas encore entrées') },
  { k: 'wait', l: key('Attendues, non contrôlées') },
];

export function EtiquettesModal({ id, lines, sourceInitiale, onClose }: {
  /** Le numéro du document (bon ou commande) — le sous-titre de la modale. */
  id: string;
  lines: LigneAEtiqueter[];
  /** La source présélectionnée — la maquette choisit `ctl` s'il y en a, sinon `done`, sinon `wait`. */
  sourceInitiale?: SourceDeQuantite;
  onClose: () => void;
}) {
  const [modeles, setModeles] = useState<LabelTemplate[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [modele, setModele] = useState<string>('');
  const [source, setSource] = useState<SourceDeQuantite>(sourceInitiale ?? 'wait');
  const [qtySaisie, setQtySaisie] = useState('');
  const [impression, setImpression] = useState(false);
  /** Les cases déjà utilisées sur la première feuille d'une planche A4 — sautées, comme dans l'impression de masse. */
  const [usedOnFirstSheet, setUsedOnFirstSheet] = useState(0);
  /** Un échantillon réel pour l'aperçu — la première ligne du document, relue du catalogue. */
  const [echantillon, setEchantillon] = useState<LabelSampleRow | null>(null);

  // ⚠️ LES GABARITS SE LISENT UNE FOIS, À L'OUVERTURE : ils ne dépendent pas du document. Les relire avec les lignes —
  //    un tableau que le bon refait à chacun de ses rendus — remettait le modèle CHOISI au premier de la liste, sous les
  //    doigts de qui venait de le changer.
  useEffect(() => {
    let vivant = true;
    api.labels()
      .then((r) => {
        if (!vivant) return;
        const visibles = [...r.standard, ...r.custom].filter((field) => !r.hidden.includes(field.key));
        setModeles(visibles);
        if (visibles.length > 0) setModele(visibles[0]!.key);
      })
      .catch((e) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, []);

  // L'échantillon suit la PREMIÈRE ligne du document — son code, pas l'identité du tableau qui la porte.
  const premier = lines[0]?.gencode ?? null;
  useEffect(() => {
    if (!premier) { setEchantillon(null); return; }
    let vivant = true;
    api.labelData([premier])
      .then((r) => { if (vivant) setEchantillon(r.rows[0] ?? null); })
      .catch(() => { if (vivant) setEchantillon(null); });
    return () => { vivant = false; };
  }, [premier]);

  const totaux = useMemo(() => ({
    done: lines.reduce((n, l) => n + l.done, 0),
    ctl: lines.reduce((n, l) => n + l.ctl, 0),
    wait: lines.reduce((n, l) => n + l.wait, 0),
  }), [lines]);

  const parDefaut = totaux[source];
  const qty = qtySaisie === '' ? parDefaut : Math.max(0, Number(qtySaisie) || 0);
  const chosen = modeles?.find((m) => m.key === modele) ?? null;

  const lancer = async () => {
    if (!chosen || qty === 0 || impression) return;
    setImpression(true);
    try {
      // ⚠️ VALEURS RELUES MAINTENANT, jamais celles de la liste (même règle que le tunnel d'étiquettes).
      const codes = lines.filter((l) => l[source] > 0).map((l) => l.gencode);
      const data = await api.labelData(codes);
      const parCode = new Map<string, LabelSampleRow>(data.rows.map((r) => [r.gencode, r]));
      const rows: PrintRow[] = [];
      const introuvables: string[] = [];
      // ⚠️ La quantité saisie PLAFONNE le total, réparti dans l'ordre des lignes — jamais une répartition
      //    inventée : réduire à 3 imprime les 3 premières pièces, c'est dit dans le title du champ.
      let reste = qty;
      for (const l of lines) {
        if (reste <= 0) break;
        const n = Math.min(l[source], reste);
        if (n <= 0) continue;
        const row = parCode.get(l.gencode);
        if (!row) { introuvables.push(l.gencode); continue; }
        reste -= n;
        rows.push({
          copies: n,
          values: labelValuesOf(row),
        });
      }
      if (rows.length === 0) {
        showToast(t('Rien à imprimer — aucune des lignes n’a pu être relue au catalogue.'), 'warn');
        return;
      }
      if (introuvables.length > 0) {
        showToast(t('{n} code introuvable au catalogue, non imprimé : {v}', { n: introuvables.length, v: introuvables.slice(0, 5).join(', ') }), 'warn');
      }
      const outcome = openPrintDocument(buildPrintDocument(chosen, rows, chosen.kind === 'a4' ? { usedOnFirstSheet } : {}));
      // ⚠️ UN ÉCHEC D'OUVERTURE SE DIT (§23.0) : sinon l'opérateur attend devant une imprimante muette.
      if (!outcome.ok) { showToast(t('L’impression n’a pas pu s’ouvrir : {reason}', { reason: outcome.reason }), 'err'); return; }
      const imprimees = rows.reduce((n, r) => n + r.copies, 0);
      showToast(t('{n} étiquette · {name} · {id}', { n: imprimees, name: chosen.name, id }));
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setImpression(false); }
  };

  const values = echantillon ? labelValuesOf(echantillon) : neutralPlateValues();

  return (
    <Modal
      open onClose={onClose} title={t('Imprimer des étiquettes')} width="lg" icon="barcode"
      subtitle={id}
      footer={(
        <>
          <span style={{ flex: 1, minWidth: 200, fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
            <Trans k='Part <1>directement à l’imprimante</1> — l’outil d’impression de masse reste pour les retirages en volume.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <button
            type="button" onClick={() => void lancer()} aria-disabled={qty === 0 || !chosen || impression || undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
              background: qty > 0 && chosen ? 'var(--ink-900)' : 'var(--ink-200)', color: qty > 0 && chosen ? 'var(--paper)' : 'var(--ink-400)',
              fontSize: '13px', fontWeight: 700, cursor: qty > 0 && chosen ? (impression ? 'progress' : 'pointer') : 'not-allowed',
            }}
          ><Icon name="printer" size={14} />{impression ? t('Impression…') : t('Imprimer maintenant')}</button>
        </>
      )}
    >
      {failure ? (
        <p role="alert" style={{ margin: 0, color: 'var(--danger-ink)', fontSize: '13px' }}>{t('Les modèles n’ont pas pu être lus : {failure}', { failure })}</p>
      ) : !modeles ? (
        <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture des modèles…')}</p>
      ) : modeles.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: '13px' }}>
          
          {t('Aucun modèle d’étiquette — créez-en un dans l’écran Étiquettes, il apparaîtra ici.')}
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 7 }}>{t('Modèle d’étiquette')}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {modeles.map((m) => {
                const on = modele === m.key;
                const roll = m.kind === 'roll';
                return (
                  <button aria-pressed={on} key={m.key} type="button" onClick={() => setModele(m.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                      border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
                      background: on ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left',
                    }}>
                    <span style={{ width: 15, height: 15, borderRadius: 'var(--r-pill)', border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-200)' }} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                      <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('{widthMm} × {heightMm} mm · {n} élément', { widthMm: m.widthMm, heightMm: m.heightMm, n: m.elements.length })}</span>
                    </span>
                    <span style={{
                      height: 18, padding: '0 7px', borderRadius: 'var(--r-xs)', fontSize: '9px', fontWeight: 800, letterSpacing: '.05em', display: 'inline-flex', alignItems: 'center', flexShrink: 0,
                      background: roll ? 'var(--teal-100)' : 'var(--violet-bg)',
                      color: roll ? 'var(--teal-800)' : 'var(--violet-fg)',
                    }}>{roll ? t('ROULEAU') : 'A4'}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div>
              <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 7 }}>{t('Aperçu')}</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, padding: '16px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
                {chosen ? (
                  <>
                    <LabelPlate template={chosen} values={values} scale={Math.min(236 / chosen.widthMm, 118 / chosen.heightMm)} />
                    <span style={{ fontSize: '11px', color: 'var(--ink-500)', textAlign: 'center' }}>{t('{widthMm} × {heightMm} mm · {n} élément', { widthMm: chosen.widthMm, heightMm: chosen.heightMm, n: chosen.elements.length })}</span>
                  </>
                ) : null}
              </div>
            </div>
            {chosen && <BarcodeWidthWarning template={chosen} />}
            <div>
              <span style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 7 }}>{t('Quantité')}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {SOURCES_QTE.map((s) => {
                  const on = source === s.k;
                  const n = totaux[s.k];
                  return (
                    <button key={s.k} type="button" onClick={() => { setSource(s.k); setQtySaisie(''); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px', cursor: 'pointer',
                        border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
                        background: on ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left',
                      }}>
                      <span style={{ width: 15, height: 15, borderRadius: 'var(--r-pill)', border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-200)' }} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t(s.l)}</span>
                      <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{t('{n} pièce', { n })}</span>
                    </button>
                  );
                })}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 9 }}
                title={t('Plafonne le nombre total d’étiquettes — réparti dans l’ordre des lignes du document')}>
                {/* ⚠️ LE CHAMP EST LE NOMBRE DE LA PHRASE : « À imprimer : [5] étiquettes » se traduit ENTIÈRE et
                    s'accorde sur la quantité retenue. Le repère 2 rend le champ lui-même — il affiche sa propre valeur,
                    d'où un rendu qui ignore le texte reçu. En trois morceaux, l'unité disait « 1 étiquettes ». */}
                <Trans k='<1>À imprimer :</1> <2>{n}</2> <3>étiquette</3>' vars={{ n: qty }} parts={[
                  (x) => <span style={{ fontSize: '12.5px', color: 'var(--ink-600)', flexShrink: 0 }}>{x}</span>,
                  () => (
                    <input
                      value={qtySaisie === '' ? String(parDefaut) : qtySaisie}
                      onChange={(e) => setQtySaisie(e.target.value.replace(/[^0-9]/g, ''))}
                      aria-label={t('Nombre d’étiquettes à imprimer')}
                      style={{ width: 70, height: 36, textAlign: 'center', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', fontWeight: 700, outline: 'none', fontVariantNumeric: 'tabular-nums' }}
                    />
                  ),
                  (x) => <span style={{ fontSize: '12px', color: 'var(--ink-500)' }}>{x}</span>,
                ]} />
              </label>
              {chosen?.kind === 'a4' && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginTop: 9 }}>
                  <UsedOnFirstSheetField value={usedOnFirstSheet} onChange={setUsedOnFirstSheet} />
                  <span style={{ fontSize: '12px', color: 'var(--ink-500)', paddingBottom: 9 }}>{t('{n} feuille A4', { n: countSheets(chosen, qty, usedOnFirstSheet) })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
